import { useEffect, useRef } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, ExternalLink } from "lucide-react";
import ProjectHeader from "../components/ProjectHeader";
import { useRouteBase } from "../context/ApiBaseContext";

/**
 * Iter-158P.1 — PDF viewer chrome preservation.
 *
 * Before this iteration, tapping "Open PDF" on the Script or Schedule
 * pages punted the producer into a raw browser tab containing only the
 * PDF stream — no OSF header, no Back control. On mobile that felt like
 * being trapped, because the only escape was the edge-swipe gesture
 * (which not everyone discovers on their own).
 *
 * This page keeps the producer inside the OSF React shell while the
 * PDF is being read:
 *
 *   • ProjectHeader stays visible above the PDF, with an explicit
 *     ← Back control that returns to the exact screen (and browser
 *     scroll position) they came from.
 *   • Desktop / Android Chrome render the PDF inline via <object>.
 *   • iOS Safari (which does not render PDFs inline in <object>) falls
 *     through to a MobilePdfHandoffCard-style body — still under the
 *     OSF header — with a clearly-labelled "Open in device viewer"
 *     secondary action for producers who want iOS's native full-screen
 *     chrome. Either way the user always has an obvious way back.
 *
 * Query params:
 *   - src       (required) — token-signed stream URL
 *   - filename  (optional) — display name (e.g. "ep04_schedule.pdf")
 *   - title     (optional) — screen title (defaults to filename)
 *   - back      (optional) — return path; if absent we use browser
 *                            history (`-1`) so the swipe-back gesture
 *                            still works
 *   - dl        (optional) — override download URL (defaults to src
 *                            with `&download=1`)
 */
export default function PdfViewerPage() {
    const { projectId } = useParams();
    const [params] = useSearchParams();
    const nav = useNavigate();
    const location = useLocation();
    const routeBase = useRouteBase();
    const src = params.get("src") || "";
    const filename = params.get("filename") || "";
    const title = params.get("title") || filename || "Document";
    const backTo = params.get("back") || "";
    const explicitDl = params.get("dl") || "";

    // Default download URL: append `download=1` to the src signed URL.
    const downloadHref = explicitDl || (() => {
        if (!src) return "";
        const sep = src.includes("?") ? "&" : "?";
        return `${src}${sep}download=1`;
    })();

    // Iter-158P.1 — Back should feel native. Prefer window.history.back()
    // (preserves scroll position on the previous screen for free) and
    // only fall back to an explicit navigate when there is no prior
    // entry (deep-link entry from an external source).
    const goBack = () => {
        if (window.history.length > 1 && window.history.state?.idx > 0) {
            nav(-1);
        } else if (backTo) {
            nav(backTo, { replace: true });
        } else if (projectId) {
            nav(`${routeBase}/${projectId}`, { replace: true });
        } else {
            nav("/", { replace: true });
        }
    };

    // Best-effort iOS Safari detection so we DON'T attempt to render the
    // <object> preview (blank on iOS) and instead present the fallback
    // card directly. Keeps the DOM light and avoids a flash of empty
    // black on load.
    const isIOS = /iPad|iPhone|iPod/.test(
        typeof navigator !== "undefined" ? navigator.userAgent : ""
    ) && !window.MSStream;

    const objectRef = useRef(null);

    // Guard: if we somehow arrived with no src, kick back to project.
    useEffect(() => {
        if (!src) goBack();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src]);

    if (!src) return null;

    const fallbackBody = (
        <div
            data-testid="pdf-viewer-fallback"
            className="rounded-xl border border-sky-500/30 bg-sky-500/[0.06] p-5 mx-auto max-w-2xl"
        >
            <h2 className="text-white font-semibold leading-tight text-base">
                Open PDF in your phone&rsquo;s viewer
            </h2>
            <p className="text-[12px] text-neutral-400 mt-1.5 leading-relaxed">
                iOS doesn&rsquo;t preview PDFs inline. Tap the button below to
                open the file in your device&rsquo;s native PDF viewer — pinch
                to zoom, scroll, rotate and search. Come back to OnSetFlow with
                the ← Back control above.
            </p>
            {filename ? (
                <p
                    data-testid="pdf-viewer-fallback-filename"
                    className="text-[11px] text-neutral-500 mt-3 break-all font-mono"
                >
                    {filename}
                </p>
            ) : null}
            <div className="mt-4 flex flex-wrap gap-2">
                <a
                    data-testid="pdf-viewer-fallback-open"
                    href={src}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="tap flex-1 min-w-[180px] inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-4 h-11 text-sm font-semibold"
                >
                    <ExternalLink size={14} />
                    Open in device viewer
                </a>
                <a
                    data-testid="pdf-viewer-fallback-download"
                    href={downloadHref}
                    download={filename || undefined}
                    className="tap inline-flex items-center justify-center gap-2 rounded-md bg-secondary border border-white/10 text-neutral-200 px-4 h-11 text-sm font-semibold"
                >
                    <Download size={14} />
                    Download
                </a>
            </div>
        </div>
    );

    return (
        <div
            data-testid="pdf-viewer-page"
            className="min-h-screen bg-neutral-950 text-neutral-100 flex flex-col"
        >
            <ProjectHeader />
            <div
                data-testid="pdf-viewer-toolbar"
                className="border-b border-white/10 bg-neutral-950/95 px-4 py-2 flex items-center gap-3"
            >
                <button
                    type="button"
                    data-testid="pdf-viewer-back"
                    onClick={goBack}
                    className="tap inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-[12px] uppercase tracking-[0.2em] text-neutral-200 border border-white/15 hover:bg-white/5"
                >
                    <ArrowLeft size={14} />
                    Back
                </button>
                <p className="min-w-0 truncate text-sm text-neutral-100" title={title}>
                    {title}
                </p>
                <div className="ml-auto flex items-center gap-2">
                    <a
                        data-testid="pdf-viewer-open-device"
                        href={src}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="tap hidden sm:inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-[11px] uppercase tracking-[0.2em] text-neutral-300 border border-white/15 hover:bg-white/5"
                        title="Open in device viewer"
                    >
                        <ExternalLink size={12} />
                        Device viewer
                    </a>
                    <a
                        data-testid="pdf-viewer-download"
                        href={downloadHref}
                        download={filename || undefined}
                        className="tap inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-[11px] uppercase tracking-[0.2em] text-neutral-300 border border-white/15 hover:bg-white/5"
                        title="Download"
                    >
                        <Download size={12} />
                        Download
                    </a>
                </div>
            </div>
            <div className="flex-1 min-h-0 bg-black">
                {isIOS ? (
                    <div className="p-4">{fallbackBody}</div>
                ) : (
                    <object
                        ref={objectRef}
                        data-testid="pdf-viewer-object"
                        type="application/pdf"
                        data={src}
                        className="w-full h-full min-h-[calc(100vh-120px)]"
                    >
                        {/* Browsers that can't render inline PDF fall through
                            to this OSF-chromed handoff. iOS Safari sometimes
                            never fires the fallback path from <object> alone;
                            the userAgent branch above catches that case. */}
                        <div className="p-4">{fallbackBody}</div>
                    </object>
                )}
            </div>
        </div>
    );
}
